import { useState, useRef, useEffect, useCallback } from "react";
import { Play, Pause, Download, RotateCcw, Check, X } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import { useButtonPress } from "@/utils/animations";
import { PetAvatar } from "@/components/PetAvatar";
import { DoodleNote, DoodleQuestion } from "@/components/Doodles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANG_LABELS = {
  'te': 'Telugu', 'hi': 'Hindi', 'en-IN': 'English', 'ta': 'Tamil',
  'kn': 'Kannada', 'ml': 'Malayalam', 'mr': 'Marathi', 'bn': 'Bengali',
  'gu': 'Gujarati', 'or': 'Odia', 'as': 'Assamese', 'mni': 'Manipuri',
};
const BOARD_LABELS = { cbse: 'CBSE', ts_ssc: 'TS SSC', icse: 'ICSE', ap_ssc: 'AP SSC', ib: 'International Baccalaureate (IB)' };

// Generate a unique session ID per browser tab
const generateSessionId = () =>
  `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const AudioPlayer = ({ generationId, audioUrl: directUrl, contentPreview, slides = [], onClose, onAudioEnded }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [slideImageUrls, setSlideImageUrls] = useState({});
  const [glowSide, setGlowSide] = useState(null); // null | 'priya' | 'bunty'
  const [audioCompleted, setAudioCompleted] = useState(false);


  const petType = localStorage.getItem('you_pet_type') || 'panda';
  const petName = localStorage.getItem('you_pet_name') || 'Your pet';

  const langCode     = localStorage.getItem('you_language') || 'te';
  const langLabel    = LANG_LABELS[langCode] || langCode;
  const boardCode    = localStorage.getItem('you_board') || 'cbse';
  const boardLabel   = BOARD_LABELS[boardCode] || boardCode.toUpperCase();

  // ── Analytics: useRef Protection Locks ─────────────────────────
  const hasLogged30Sec = useRef(false);
  const hasLoggedCompleted = useRef(false);
  const sessionIdRef = useRef(generateSessionId());
  const blobUrlsRef = useRef({});

  // Prefer direct Supabase Storage URL, fallback to backend proxy
  const audioUrl = directUrl || `${BACKEND_URL}/api/audio/${generationId}`;

  // Button press hooks
  const playPress     = useButtonPress();
  const rewindPress   = useButtonPress();
  const downloadPress = useButtonPress();

  // ── Reset flags when track changes ────────────────────────────
  useEffect(() => {
    hasLogged30Sec.current = false;
    hasLoggedCompleted.current = false;
    sessionIdRef.current = generateSessionId();
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setDownloadProgress(null);
    setAudioCompleted(false);
  }, [generationId, directUrl]);

  // ── Reset slide index when slides change ──────────────────────
  useEffect(() => {
    setCurrentSlideIndex(0);
  }, [slides]);

  // ── Speaker glow (15s alternation, only while playing) ────────
  useEffect(() => {
    if (!isPlaying) { setGlowSide(null); return; }
    setGlowSide('priya');
    const t = setInterval(() => setGlowSide(s => s === 'priya' ? 'bunty' : 'priya'), 15000);
    return () => clearInterval(t);
  }, [isPlaying]);

  // ── Fetch ALL slide images as blobs simultaneously ─────────────
  useEffect(() => {
    if (!slides || slides.length === 0) return;
    Object.values(blobUrlsRef.current).forEach((url) => {
      if (url && url !== "failed") URL.revokeObjectURL(url);
    });
    blobUrlsRef.current = {};
    setSlideImageUrls({});

    slides.forEach((slide, i) => {
      if (slide.imageUrl) {
        fetch(slide.imageUrl)
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.blob();
          })
          .then((blob) => {
            if (blob.size < 1000) {
              console.warn(`Slide ${i} image too small (${blob.size} bytes), treating as failed`);
              blobUrlsRef.current[i] = "failed";
              setSlideImageUrls((prev) => ({ ...prev, [i]: "failed" }));
              return;
            }
            const objectUrl = URL.createObjectURL(blob);
            blobUrlsRef.current[i] = objectUrl;
            setSlideImageUrls((prev) => ({ ...prev, [i]: objectUrl }));
          })
          .catch((err) => {
            console.warn(`Slide ${i} blob fetch failed:`, err);
            blobUrlsRef.current[i] = "failed";
            setSlideImageUrls((prev) => ({ ...prev, [i]: "failed" }));
          });
      } else {
        blobUrlsRef.current[i] = "failed";
        setSlideImageUrls((prev) => ({ ...prev, [i]: "failed" }));
      }
    });

    return () => {
      Object.values(blobUrlsRef.current).forEach((url) => {
        if (url && url !== "failed") URL.revokeObjectURL(url);
      });
      blobUrlsRef.current = {};
    };
  }, [slides]);

  // ── Log milestone to Supabase (fire-and-forget) ───────────────
  const logMilestone = useCallback(async (milestone) => {
    try {
      await supabase.from("listen_metrics").insert({
        session_id: sessionIdRef.current,
        topic_played: generationId,
        milestone,
      });
    } catch (err) {
      console.warn("Analytics log failed:", err);
    }
  }, [generationId]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => setDuration(audio.duration);

    const onTimeUpdate = () => {
      const t = audio.currentTime;
      setCurrentTime(t);
      if (t >= 30.0 && !hasLogged30Sec.current) {
        hasLogged30Sec.current = true;
        logMilestone("30_seconds");
      }
    };

    const onEnded = () => {
      setIsPlaying(false);
      setAudioCompleted(true);
      if (!hasLoggedCompleted.current) {
        hasLoggedCompleted.current = true;
        logMilestone("completed");
      }
      if (onAudioEnded) onAudioEnded();
    };

    const onError = (e) => {
      console.error("Audio load error:", e);
      if (directUrl && audio.src === directUrl) {
        audio.src = `${BACKEND_URL}/api/audio/${generationId}`;
        audio.load();
      }
    };

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [directUrl, generationId, logMilestone, onAudioEnded]);

  const safePlay = async (audio) => {
    try { await audio.play(); }
    catch (err) { if (err.name !== 'AbortError') console.error('Audio play error:', err); }
  };

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      safePlay(audio);
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleSeek = useCallback((value) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value[0];
    setCurrentTime(value[0]);
  }, []);

  const restart = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    setCurrentTime(0);
    safePlay(audio);
    setIsPlaying(true);
  }, []);

  const handleDownload = useCallback(async () => {
    setDownloadProgress("downloading");
    try {
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `you_story_${generationId.slice(0, 8)}.ogg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setDownloadProgress("done");
      setTimeout(() => setDownloadProgress(null), 2000);
    } catch (err) {
      console.error("Download failed:", err);
      setDownloadProgress(null);
    }
  }, [audioUrl, generationId]);

  const handleWhatsApp = useCallback(() => {
    const msg = `🎧 Just learned about ${contentPreview} through a fun story!\n` +
      `Try YOU free — stories in Telugu, Hindi & more:\n` +
      `https://youinfotainment.com`;
    window.open('https://wa.me/?text=' + encodeURIComponent(msg), '_blank');
  }, [contentPreview]);

  const formatTime = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current || slides.length === 0) return;
    const { currentTime: ct, duration: dur } = audioRef.current;
    if (!dur) return;
    const currentProgress = (ct / dur) * 100;
    let newSlideIndex = 0;
    for (let i = slides.length - 1; i >= 0; i--) {
      if (slides[i].startTimePercentage <= currentProgress) {
        newSlideIndex = i;
        break;
      }
    }
    if (newSlideIndex !== currentSlideIndex) {
      setCurrentSlideIndex(newSlideIndex);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 200, damping: 25 }}
      style={{ background: '#FFFFFF', border: '1.5px dashed #CCCCCC', borderRadius: 4, overflow: 'hidden', width: '100%', position: 'relative' }}
      data-testid="audio-player"
    >
      {/* Doodle decorations */}
      <div style={{ position: 'absolute', top: 10, right: 10, pointerEvents: 'none' }}>
        <DoodleNote color="#448AFF" opacity={0.15} size={12} />
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <audio ref={audioRef} src={audioUrl} preload="metadata" crossOrigin="anonymous" onTimeUpdate={handleTimeUpdate} />

      {/* 1 — Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px 8px' }}>
        {/* ← back */}
        <button
          onClick={onClose}
          style={{ color: '#666666', fontSize: 14, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
          data-testid="close-btn"
        >←</button>

        {/* Title + subtitle */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 24, fontFamily: "'Patrick Hand', cursive", fontWeight: 400, color: '#1A1A2E', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {contentPreview || 'Your Story'}
          </p>
          <p style={{ fontSize: 15, color: '#4A4A5A', margin: 0 }}>
            {langLabel} · {duration > 0 ? `${Math.max(1, Math.round(duration / 60))} min story` : 'Audio story'} · {boardLabel}
          </p>
        </div>

        {/* WhatsApp share */}
        <button
          onClick={handleWhatsApp}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 14px', borderRadius: 14, background: 'transparent',
            border: '1.5px dashed #25D366', cursor: 'pointer', flexShrink: 0,
            fontSize: 14, fontFamily: "'Patrick Hand', cursive", fontWeight: 400, color: '#25D366',
            minHeight: 44,
          }}
          data-testid="whatsapp-btn"
        >Share 💚</button>
      </div>

      {/* 2 — Slide area (stacked full-width) */}
      {slides.length > 0 && (() => {
        const slide = slides[currentSlideIndex];
        const imgUrl = slideImageUrls[currentSlideIndex];
        const glowStyle = glowSide === 'priya'
          ? 'inset -3px 0 12px rgba(68,138,255,0.08)'
          : glowSide === 'bunty'
          ? 'inset 3px 0 12px rgba(255,215,64,0.08)'
          : 'none';
        return (
          <div style={{ padding: '0 14px', marginBottom: 10 }}>
            {/* Image container */}
            <div style={{
              width: '100%', borderRadius: 4, overflow: 'hidden',
              position: 'relative', backgroundColor: 'rgba(0,0,0,0.04)',
              border: '1.5px dashed #CCCCCC', transition: 'border-color 1s ease-in-out',
              minHeight: 100,
            }}>
              {imgUrl && imgUrl !== 'failed' ? (
                <img src={imgUrl} alt={slide?.title} style={{ width: '100%', height: 'auto', display: 'block', objectFit: 'contain' }} />
              ) : imgUrl === 'failed' ? (
                <div style={{
                  width: '100%', height: 160, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 8,
                  background: 'linear-gradient(135deg, #E8F0FE 0%, #F3E8FD 100%)',
                }}>
                  <span style={{ fontSize: 40 }}>📖</span>
                  <span style={{
                    fontSize: 15, fontFamily: "'Patrick Hand', cursive",
                    color: '#448AFF', textAlign: 'center', padding: '0 16px',
                    maxWidth: 260,
                  }}>
                    {slide?.title || 'Imagine the scene...'}
                  </span>
                </div>
              ) : (
                <div style={{ width: '100%', height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid #448AFF', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
                </div>
              )}
              {/* Progress dots overlaid at bottom center — hide for single thumbnail */}
              {slides.length > 1 && (
              <div style={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 4 }}>
                {slides.map((_, i) => (
                  <div key={i} style={{
                    height: 3,
                    width: i === currentSlideIndex ? 10 : 3,
                    borderRadius: 2,
                    background: i === currentSlideIndex ? '#448AFF' : 'rgba(0,0,0,0.15)',
                    transition: 'width 0.3s ease',
                  }} />
                ))}
              </div>
              )}
            </div>
            {/* Title + content below image */}
            {slide && (
              <div style={{ padding: '8px 2px 0' }}>
                <p style={{ fontSize: 17, fontFamily: "'Patrick Hand', cursive", fontWeight: 400, color: '#1A1A2E', margin: '0 0 3px 0' }}>{slide.title}</p>
                <p style={{ fontSize: 16, color: '#4A4A5A', margin: 0, lineHeight: 1.4 }}>{slide.content}</p>
              </div>
            )}
          </div>
        );
      })()}

      {/* 3 — Seek bar (custom, 4px height) */}
      <div style={{ padding: '0 14px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#666666', fontFamily: 'monospace', width: 32, textAlign: 'right' }} data-testid="current-time">
            {formatTime(currentTime)}
          </span>
          <div
            style={{ flex: 1, height: 4, borderRadius: 2, background: '#F0EBE6', border: '1px dashed #CCCCCC', position: 'relative', cursor: 'pointer' }}
            onClick={e => {
              const rect = e.currentTarget.getBoundingClientRect();
              const pct  = (e.clientX - rect.left) / rect.width;
              handleSeek([Math.max(0, Math.min(pct * (duration || 0), duration || 0))]);
            }}
            data-testid="seek-slider"
          >
            <div style={{
              position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 2,
              background: '#448AFF',
              width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%',
            }} />
          </div>
          <span style={{ fontSize: 12, color: '#666666', fontFamily: 'monospace', width: 32 }} data-testid="total-duration">
            {formatTime(duration)}
          </span>
        </div>
      </div>

      {/* 4 — Controls (rewind · play · download) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 28, marginBottom: 16 }}>
        {/* Rewind */}
        <button
          onClick={restart}
          {...rewindPress}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#555555', fontSize: 22, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', ...rewindPress.style }}
          data-testid="restart-btn"
        >
          <RotateCcw size={24} />
        </button>

        {/* Play/Pause 56px */}
        <button
          onClick={togglePlay}
          {...playPress}
          style={{
            width: 56, height: 56, borderRadius: '50%',
            background: '#448AFF',
            border: '1.5px dashed #448AFF', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', ...playPress.style,
          }}
          data-testid="play-pause-btn"
        >
          {isPlaying
            ? <Pause size={24} />
            : <Play size={24} style={{ marginLeft: 2 }} />
          }
        </button>

        {/* Download */}
        <button
          onClick={handleDownload}
          {...downloadPress}
          disabled={downloadProgress === 'downloading'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#555555', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', ...downloadPress.style }}
          data-testid="download-btn"
        >
          {downloadProgress === 'done' ? <Check size={22} style={{ color: '#2E7D32' }} /> :
           downloadProgress === 'downloading' ? <Download size={22} style={{ opacity: 0.5 }} /> :
           <Download size={22} />}
        </button>
      </div>

      {/* 5 — Pet companion row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 14px', marginBottom: 12 }}>
        <PetAvatar
          petType={petType}
          stage={parseInt(localStorage.getItem('you_pet_stage') || '1')}
          mood={audioCompleted ? 'happy' : isPlaying ? 'listening' : 'curious'}
          size={72}
          animated
        />
        <div style={{ background: 'rgba(68,138,255,0.04)', borderRadius: 12, padding: '10px 14px', flex: 1, border: '1.5px dashed #448AFF' }}>
          <p style={{ fontSize: 17, fontFamily: "'Patrick Hand', cursive", color: '#1A1A2E', margin: '0 0 3px 0', fontWeight: 400 }}>
            {petName} is listening with you! 🎧
          </p>
          <p style={{ fontSize: 13, fontFamily: "'Inter', sans-serif", color: '#555555', margin: 0, fontWeight: 400 }}>
            {parseInt(localStorage.getItem('you_pet_stars') || '0')} ⭐ total
          </p>
        </div>
      </div>


    </motion.div>
  );
};
